import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  ERROR_LEVEL_FILTER,
  LEVEL_FILTERS,
  type LogLevelKey,
} from '@/lib/logLevels';

export interface ActivityLogRow {
  id: string;
  level: string;
  module: string;
  action: string;
  result: string;
  description: string | null;
  user_id: string | null;
  user_name: string | null;
  role: string | null;
  branch_id: string | null;
  target_type: string | null;
  target_id: string | null;
  ip: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface LogFilters {
  level: string | null;
  module: string | null;
  result: string | null;
  search: string;
  errorsOnly: boolean;
  rangeDays: number;
}

export interface LogCounts {
  errors: number;
  critical: number;
  today: number;
}

const PAGE_SIZE = 40;

export function useActivityLogs(filters: LogFilters) {
  const [rows, setRows] = useState<ActivityLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [modules, setModules] = useState<string[]>([]);
  const [counts, setCounts] = useState<LogCounts>({ errors: 0, critical: 0, today: 0 });
  const pageRef = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buildQuery = useCallback(() => {
    const since = new Date(Date.now() - filters.rangeDays * 86400000).toISOString();
    let query = supabase
      .from('activity_logs')
      .select('*')
      .gte('created_at', since)
      .order('created_at', { ascending: false });

    if (filters.errorsOnly) {
      query = query.or(ERROR_LEVEL_FILTER);
    } else if (filters.level) {
      const levelFilter = LEVEL_FILTERS[filters.level as LogLevelKey];
      if (levelFilter) query = query.or(levelFilter);
    }
    if (filters.module) query = query.eq('module', filters.module);
    if (filters.result) query = query.eq('result', filters.result);

    const term = filters.search.trim().replace(/[%_]/g, '');
    if (term) {
      query = query.or(
        `action.ilike.%${term}%,description.ilike.%${term}%,user_name.ilike.%${term}%,target_id.ilike.%${term}%`
      );
    }
    return query;
  }, [
    filters.level,
    filters.module,
    filters.result,
    filters.search,
    filters.errorsOnly,
    filters.rangeDays,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    pageRef.current = 0;
    try {
      const { data, error: e } = await buildQuery().range(0, PAGE_SIZE - 1);
      if (e) throw e;
      const list = (data as ActivityLogRow[]) ?? [];
      setRows(list);
      setHasMore(list.length === PAGE_SIZE);
      pageRef.current = 1;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la actividad');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const from = pageRef.current * PAGE_SIZE;
      const { data, error: e } = await buildQuery().range(from, from + PAGE_SIZE - 1);
      if (e) throw e;
      const list = (data as ActivityLogRow[]) ?? [];
      setRows((prev) => [...prev, ...list]);
      setHasMore(list.length === PAGE_SIZE);
      pageRef.current += 1;
    } catch {
      /* se conserva lo ya cargado */
    } finally {
      setLoadingMore(false);
    }
  }, [buildQuery]);

  const loadMeta = useCallback(async () => {
    try {
      const since = new Date(Date.now() - filters.rangeDays * 86400000).toISOString();
      const dayStart = new Date();
      dayStart.setHours(0, 0, 0, 0);
      const [errRes, critRes, todayRes, modRes] = await Promise.all([
        supabase
          .from('activity_logs')
          .select('id', { count: 'exact', head: true })
          .or(LEVEL_FILTERS.error)
          .gte('created_at', since),
        supabase
          .from('activity_logs')
          .select('id', { count: 'exact', head: true })
          .or(LEVEL_FILTERS.critical)
          .gte('created_at', since),
        supabase
          .from('activity_logs')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', dayStart.toISOString()),
        supabase.from('activity_logs').select('module').gte('created_at', since),
      ]);
      setCounts({
        errors: errRes.count ?? 0,
        critical: critRes.count ?? 0,
        today: todayRes.count ?? 0,
      });
      const mods = new Set<string>();
      for (const r of (modRes.data ?? []) as { module: string }[]) {
        if (r.module) mods.add(r.module);
      }
      setModules([...mods].sort());
    } catch {
      /* los contadores son informativos */
    }
  }, [filters.rangeDays]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const reload = useCallback(() => {
    load();
    loadMeta();
  }, [load, loadMeta]);

  return { rows, loading, loadingMore, error, hasMore, loadMore, modules, counts, reload };
}